import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import types from 'actions/types'
import {generalError} from 'actions/index'
import {
  addJobCardFailure,
  addJobCardsFailure,
  filterJobCardsForDateRange as filterJobCardsForDateRangeAction,
  getOrderCountForBulkRemoveFailure,
  getOrderCountForBulkRemoveSuccess,
  hideBulkRemoveFromJobBoardDialog,
  loadConfirmedJobs as loadConfirmedJobsAction,
  loadConfirmedJobsCount as loadConfirmedJobsCountAction,
  loadConfirmedJobsCountFailure,
  loadConfirmedJobsCountSuccess,
  loadConfirmedJobsFailure,
  loadConfirmedJobsSuccess,
  loadJobCardsFailure,
  loadJobCardsSuccess,
  removeOrderFromJobBoardFailure,
  removeOrdersFromJobBoardFailure,
  removeOrdersFromJobBoardSuccess,
  setConfirmedJobs,
  updateConfirmedOrderCount,
  updateJobCardFailure,
  updateJobCardSuccess,
} from 'actions/production'
import {
  getConfirmedOrders,
  getConfirmedOrdersCount,
  getJobBoardFilters,
  getJobBoardListViewSelector,
  getJobCards,
  getProductionDepartments,
  getProductionWorkCentres,
  isObserving,
} from 'selectors/jobBoards'
import {get, isNil} from 'lodash'
import {snackbarMessage} from 'actions/snackbar'
import {filterJobCardsByDateRange} from 'helpers/production/jobBoardFilters'
import {createDocumentPivotTableWatcher, createPivotTableWatcher} from 'sagas/pivotTable'
import {setupJobCardsDialogPivotTableKey} from 'reducers/dialogs/setupJobCardDialog'
import {jobCardListViewPivotTableKey} from 'reducers/productionContext/jobBoards/jobCardListView'
import {getSetupJobCardDialog, getViewJobCardDialog} from 'selectors/dialog'
import {getAllRows, getAllSelectedRows, getSearch} from 'selectors/pivotTable'
import {
  getConfirmedJobParams,
  getJobBoardFilterParams,
  getJobCardLineItems,
  getUpdatedConfirmedJobs,
  getUpdatedConfirmedJobsCount,
  JobBoardStatus,
  prepareJobCardLineItems,
  shouldExistInOrderBook,
} from 'helpers/production'
import {updateData} from 'actions/pivotTable'
import {hideSkill, skillPage} from 'actions/onboarding'
import {SKILLS} from 'helpers/onboarding'
import {isInSkill} from 'selectors/onboarding'
import {hideViewJobCardDialog} from 'actions/dialog'
import {getSelectableOrderItems} from 'helpers/production/setupJobCardDialog'
import {getUser} from 'selectors/auth'
import {getDateFilters} from 'helpers/dateRange'
import {DeliveryStatus} from 'helpers/deliveries'
import {getInvoiceFinancialYearStart, isProductionDisabled} from 'selectors/settings'
import {urls} from 'helpers/url'
import {push} from 'react-router-redux'
import {iconNames} from 'components/icon'
import {statusesToExcludeFromProduction} from 'helpers/quotes'
import {jobCardDialogDocumentsPivotTableKey, jobCardOrderItemsPivotTableKey} from 'reducers/dialogs/viewJobCardDialog'
import {getOrderDocumentsFolderPath} from 'helpers/documents'

export const orderItemFields = {
  lineItemLetter: true,
  partCode: true,
  quantity: true,
  allSubItems: true,
}

export const orderFields = {
  default: true,
  overrides: true,
  company: {
    name: true,
  },
  estimator: true,
  salesman: true,
  contact: {
    isEmailValid: true,
    location: true,
    name: true,
  },
  orderItems: {
    id: true,
    dormant: true,
    desc: true,
    notes: true,
    ...orderItemFields,
  },
  jobCards: {
    id: true,
    orderId: true,
    orderItemId: true,
    owner: true,
  },
  allItems: true,
}

export const jobCardFields = {
  default: true,
  order: orderFields,
  orderItem: orderItemFields,
  jobCardDepartments: true,
  jobCardOwners: {
    default: true,
    contact: true,
  },
  jobCardHistory: {
    default: true,
    contact: {fullName: true},
  },
}

export const listViewJobCardFields = {
  ...jobCardFields,
  order: {
    ...orderFields,
    deliveryNotes: {
      default: true,
      deliveryNoteItems: true,
    },
    userFields: true,
  },
}

export default (api) => {
  function* loadJobCards() {
    try {
      const params = {
        sortColumn: 'sortOrder',
        excludeDelivered: true,
      }

      const response = yield call(api.jobCards.get, params, jobCardFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadJobCardsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadJobCardsFailure(message, code))
            yield put(generalError(message))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updatedJobBoardFilters({filters}) {
    try {

      const {searchWithPartCode, targetStart, targetEnd, selectedOrderTypeIds} = filters
      if (searchWithPartCode !== undefined ||
          targetStart !== undefined ||
          targetEnd !== undefined ||
          selectedOrderTypeIds !== undefined) {

        yield put(loadConfirmedJobsAction())
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadConfirmedJobs({page = 0, size = 25}) {
    try {
      const filters = yield select(state => getJobBoardFilters(state))
      const {searchWithPartCode, selectedOrderTypeIds} = filters
      const {invoiceFinancialYearStart} = yield select(state => getUser(state).settings)
      const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

      const params = getConfirmedJobParams(searchWithPartCode, targetStart, targetEnd, page, size, selectedOrderTypeIds)

      const response = yield call(api.quote.get, params, orderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadConfirmedJobsSuccess(data.result, data.count, page))
          },
          function* onFail(key, message, code) {
            yield put(loadConfirmedJobsFailure(message, code))
            yield put(generalError(message))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadConfirmedJobsCount() {
    try {
      const filters = yield select(state => getJobBoardFilters(state))
      const {
        searchWithPartCode,
        orderTypeId,
      } = filters
      const {invoiceFinancialYearStart} = yield select(state => getUser(state).settings)
      const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

      const params = getConfirmedJobParams(searchWithPartCode, targetStart, targetEnd, 0, 0, orderTypeId)

      const response = yield call(api.quote.get, params, orderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadConfirmedJobsCountSuccess(data.count))
          },
          function* onFail(key, message, code) {
            yield put(generalError(message))
            yield put(loadConfirmedJobsCountFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* actionFailure({message, error}) {
    try {
      yield put(snackbarMessage(message || error, 'error'))
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addJobCard({
                         order,
                         selectedDepartments,
                         selectedOwners,
                         sortOrder,
                         targetDate,
                         productionNotes,
                         jobCardNotes,
                         requiredDate,
                       }) {
    try {
      const params = {
        orderId: order.id,
        productionDepartments: selectedDepartments,
        owners: selectedOwners.map(owner => {
          return {
            jobCardId: -1,
            contactId: owner,
          }
        }),
        sortOrder,
        targetDate,
        productionNotes,
        jobCardNotes,
        requiredDate,
      }

      const response = yield call(api.jobCards.add, params, jobCardFields)
      yield api.handleResponse(
          response,
          function* onOk() {
            // Don't emit an action here as it will come back via the web socket
          },
          function* onFail(key, message, code) {
            yield put(addJobCardFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addJobCards({orderId, sortOrder, targetDate, productionNotes, isProductionScheduled, requiredDate}) {
    try {
      const jobCards = yield select(getJobCards)
      const selectedOrderItems = yield select(state =>
          getSelectableOrderItems(getAllSelectedRows(state, setupJobCardsDialogPivotTableKey), jobCards)
      )
      const orderItems = getJobCardLineItems(selectedOrderItems, sortOrder)

      const params = {
        orderId,
        orderItems,
        targetDate,
        productionNotes,
        isProductionScheduled,
        requiredDate,
      }

      const response = yield call(api.jobCards.addItems, params, jobCardFields)
      yield api.handleResponse(
          response,
          function* onOk() {
            // Don't emit an action here as it will come back via the web socket
          },
          function* onFail(key, message, code) {
            yield put(addJobCardsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateJobCard({jobCard, closeDialog}) {
    try {

      const owners = isNil(jobCard.owners)
          ? jobCard.jobCardOwners
          : jobCard.owners.map(owner => {
            return {
              jobCardId: jobCard.id,
              contactId: owner,
            }
          })

      const params = {
        id: jobCard.id,
        departmentId: jobCard.departmentId,
        workCentreId: jobCard.workCentreId,
        sortOrder: jobCard.sortOrder,
        departmentStatus: jobCard.departmentStatus,
        status: jobCard.status,
        description: jobCard.description,
        notes: jobCard.notes,
        onHoldReasonId: jobCard.onHoldReasonId,
        onHoldNotes: jobCard.onHoldNotes,
        productionDepartments: jobCard.jobCardDepartments,
        owners,
        requiredDate: jobCard.order.requiredDate,
        targetDate: jobCard.order.targetDate,
        productionNotes: jobCard.order.productionNotes,
      }

      const response = yield call(api.jobCards.update, params, jobCardFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            // Don't emit an action here as it will come back via the web socket
            const isObservingJobCards = yield select(isObserving)
            if (!isObservingJobCards) {
              yield put(updateJobCardSuccess(data))
            }
            if (closeDialog) yield put(hideViewJobCardDialog())
          },
          function* onFail(key, message, code) {
            yield put(updateJobCardFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* filterJobCardsForDateRange() {
    const mainRoute = yield select(state => state.routes.trackedRoutes.main)
    if (mainRoute === 'job-boards') {
      const {targetStart, targetEnd} = yield select(state => state.productionContext.jobBoards.filters)
      const confirmedOrderCount = yield select(state => state.productionContext.jobBoards.confirmedOrders.count)
      const confirmedOrders = yield select(state => state.productionContext.jobBoards.confirmedOrders.data)
      const updatedConfirmedOrders = filterJobCardsByDateRange(confirmedOrders, 'targetDate', targetStart, targetEnd)
      yield put(filterJobCardsForDateRangeAction(updatedConfirmedOrders))
      if (updatedConfirmedOrders.length < confirmedOrders.length)
        yield put(updateConfirmedOrderCount(confirmedOrderCount - 1))
    }
  }

  function* setupOrderItemsForJobCard({key}) {
    if (key === setupJobCardsDialogPivotTableKey) {
      const productionDepartments = yield select(getProductionDepartments)
      const productionWorkCentres = yield select(getProductionWorkCentres)
      const allRows = yield select(state => getAllRows(state, setupJobCardsDialogPivotTableKey))
      const orderItems = prepareJobCardLineItems(
          allRows,
          productionDepartments,
          productionWorkCentres)
      yield put(updateData(setupJobCardsDialogPivotTableKey, orderItems))
    }
  }

  const getJobCardSetupSkillPage = () => SKILLS.JOB_BOARD.steps.findIndex(step => step.isJobCardSetupStep)

  function* showingJobCardSetupDialog() {
    try {
      const inSkill = yield select(state => isInSkill(state, SKILLS.JOB_BOARD))
      if (inSkill) yield put(skillPage(SKILLS.JOB_BOARD.key, getJobCardSetupSkillPage()))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* hidingJobCardSetupDialog() {
    try {
      const inSkill = yield select(state => isInSkill(state, SKILLS.JOB_BOARD))
      if (inSkill) yield put(skillPage(SKILLS.JOB_BOARD.key, getJobCardSetupSkillPage() + 1))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* skillStarted({key}) {
    if (key === SKILLS.JOB_BOARD.key) {
      const productionDisabled = yield select(isProductionDisabled)
      if (productionDisabled) {
        yield put(hideSkill(key, false))
        yield put(push(urls.jobBoards.baseUrl))
      }
    }
  }

  function* getOrderCountForBulkRemove({status, confirmedStart, confirmedEnd}) {
    try {
      const params = {
        status,
        confirmedStart,
        confirmedEnd,
        excludeStatus: statusesToExcludeFromProduction,
        jobBoardStatus: JobBoardStatus.notScheduled,
        deliveryStatus: [
          DeliveryStatus.partDelivered,
          DeliveryStatus.none,
        ],
        results: false,
        count: true,
      }

      const response = yield call(api.quote.get, params)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(getOrderCountForBulkRemoveSuccess(data))
          },
          function* onFail(key, message, code) {
            yield put(getOrderCountForBulkRemoveFailure(message, code))
            yield put(snackbarMessage(message, 'error', false))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* removeOrderFromJobBoard({orderId}) {
    try {
      yield put(snackbarMessage("Removing job...", 'loading', false, false))

      const params = {orderId}
      const response = yield call(api.quote.removeFromJobBoard, params)

      yield api.handleResponse(
          response,
          function* onOk() {
            // Note: success action will come from the server via socket
            yield put(snackbarMessage('Job removed', iconNames.tick, true, true))
          },
          function* onFail(key, message, code) {
            yield put(removeOrderFromJobBoardFailure(message, code))
            yield put(snackbarMessage(message, 'error', false))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* removeOrdersFromJobBoard({status, confirmedStart, confirmedEnd}) {
    try {

      const params = {
        status,
        confirmedStart,
        confirmedEnd,
      }

      const response = yield call(api.quote.removeFromJobBoard, params)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(removeOrdersFromJobBoardSuccess())
            yield put(hideBulkRemoveFromJobBoardDialog())
            yield put(loadConfirmedJobsAction())

            const affectedOrders = data.result
            const message = `${affectedOrders} job(s) have been removed`
            yield put(snackbarMessage(message, iconNames.tick, false))
          },
          function* onFail(key, message, code) {
            yield put(removeOrdersFromJobBoardFailure(message, code))
            yield put(snackbarMessage(message, 'error', false))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  //
  function* updateConfirmedOrders({data}) {
    try {
      const updatedOrder = data.result
      const confirmedOrders = yield select(getConfirmedOrders)
      const confirmedOrdersCount = yield select(getConfirmedOrdersCount)
      const filters = yield select(getJobBoardFilters)

      const {search} = filters
      const {invoiceFinancialYearStart} = yield select(state => getUser(state).settings)
      const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

      const shouldExist = shouldExistInOrderBook(updatedOrder, search, targetStart, targetEnd)
      const updatedConfirmedJobs = getUpdatedConfirmedJobs(confirmedOrders, updatedOrder, shouldExist)

      yield put(setConfirmedJobs(updatedConfirmedJobs))

      const updatedConfirmedJobsCount = getUpdatedConfirmedJobsCount(
          confirmedOrders,
          confirmedOrdersCount,
          updatedOrder,
          updatedConfirmedJobs)

      if (!isNil(updatedConfirmedJobsCount)) {
        yield put(loadConfirmedJobsCountSuccess(updatedConfirmedJobsCount))
      } else {
        yield put(loadConfirmedJobsCountAction())
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.PRODUCTION_LOAD_JOB_CARDS_ATTEMPT, loadJobCards),

      takeLatest(types.PRODUCTION_LOAD_CONFIRMED_JOBS_ATTEMPT, loadConfirmedJobs),
      takeLatest(types.PRODUCTION_LOAD_CONFIRMED_JOBS_FAILURE, actionFailure),

      takeLatest(types.PRODUCTION_LOAD_CONFIRMED_JOBS_COUNT_ATTEMPT, loadConfirmedJobsCount),
      takeLatest(types.PRODUCTION_LOAD_CONFIRMED_JOBS_COUNT_FAILURE, actionFailure),

      takeLatest(types.PRODUCTION_UPDATE_JOB_BOARD_FILTERS, updatedJobBoardFilters),
      takeLatest(types.PRODUCTION_ADD_JOB_CARD_ATTEMPT, addJobCard),
      takeLatest(types.PRODUCTION_ADD_JOB_CARD_FAILURE, actionFailure),
      takeLatest(types.PRODUCTION_ADD_JOB_CARDS_ATTEMPT, addJobCards),
      takeLatest(types.PRODUCTION_ADD_JOB_CARDS_FAILURE, actionFailure),
      takeLatest(types.PRODUCTION_UPDATE_JOB_CARD_ATTEMPT, updateJobCard),
      takeLatest(types.PRODUCTION_UPDATE_JOB_CARD_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOG_CHANGE, filterJobCardsForDateRange),
      takeLatest(types.PIVOT_TABLE_LOAD_SUCCESS, setupOrderItemsForJobCard),
      takeLatest(types.PRODUCTION_SHOW_SETUP_JOB_CARD_DIALOG, showingJobCardSetupDialog),
      takeLatest(types.PRODUCTION_HIDE_SETUP_JOB_CARD_DIALOG, hidingJobCardSetupDialog),
      takeLatest(types.ONBOARDING_SKILL_SHOW, skillStarted),
      takeLatest(types.PRODUCTION_GET_ORDER_COUNT_FOR_BULK_REMOVE_ATTEMPT, getOrderCountForBulkRemove),
      takeLatest(types.PRODUCTION_REMOVE_ORDER_FROM_JOB_BOARD_ATTEMPT, removeOrderFromJobBoard),
      takeLatest(types.PRODUCTION_REMOVE_ORDERS_FROM_JOB_BOARD_ATTEMPT, removeOrdersFromJobBoard),

      takeLatest(types.QUOTING_QUOTE_CHANGE_ORDER_STATUS_SUCCESS, updateConfirmedOrders),
      takeLatest(types.QUOTING_QUOTE_SAVE_RECEIVED, updateConfirmedOrders),
    ])
  }

  const setupJobCardDialogPivotTable = createPivotTableWatcher(
      api.quoteItem,
      setupJobCardsDialogPivotTableKey,
      [
        types.PRODUCTION_SHOW_SETUP_JOB_CARD_DIALOG,
      ],
      state => {
        const {order} = getSetupJobCardDialog(state)
        return {
          limit: 2000,
          orderId: order.id,
          includeDormant: false,
        }
      },
      {
        id: true,
        orderId: true,
        desc: true,
        quantity: true,
        order: {
          deliveryNotes: {
            status: true,
            returnNote: true,
            deliveryNoteItems: {
              orderItemId: true,
              quantity: true,
            },
          },
        },
        lineItemLetter: true,
        notes: true,
        jobCard: {
          id: true,
        },
      }
  )

  const jobCardsListViewPivotTable = createPivotTableWatcher(
      api.jobCards,
      jobCardListViewPivotTableKey,
      [
        types.PRODUCTION_UPDATE_JOB_CARD_SUCCESS,
        types.PRODUCTION_UPDATE_JOB_BOARD_LIST_VIEW_FILTERS,

      ],
      (state) => {
        const filters = getJobBoardListViewSelector(state)
        const {
          statusTypes,
          orderTypeId,
          companies,
          dateTypes,
          selectedDepartments,
          selectedWorkCentres,
          selectedRoles,
          selectedContacts,
        } = filters

        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

        return getJobBoardFilterParams(
            getSearch(state, jobCardListViewPivotTableKey),
            statusTypes,
            orderTypeId,
            companies,
            dateTypes,
            targetStart,
            targetEnd,
            selectedDepartments,
            selectedWorkCentres,
            selectedRoles,
            selectedContacts)
      },
      listViewJobCardFields
  )

  const jobCardQuoteDocumentsPivotTable = createDocumentPivotTableWatcher(
      api.document,
      jobCardDialogDocumentsPivotTableKey,
      [types.JOB_CARD_DOCUMENTS_SET_SELECTED_SUB_FOLDER],
      state => {
        const viewJobCardDialog = getViewJobCardDialog(state)
        const order = get(viewJobCardDialog, 'order')
        const baseFolder = getOrderDocumentsFolderPath(order)
        const subFolder = get(viewJobCardDialog, 'documentSubFolder') || ''

        return {
          documentProvider: "DEFAULT",
          folderPath: baseFolder + subFolder,
          fromRoot: false,
        }
      }
  )

  const jobCardQuoteItemsPivotTable = createPivotTableWatcher(
      api.quoteItem,
      jobCardOrderItemsPivotTableKey,
      [],
      state => {
        return {
          orderId: state.dialogs.viewJobCard.jobCard.orderId,
        }
      },
      {
        default: true,
        subitems: true,
        userFields: true,
      }
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(setupJobCardDialogPivotTable)
    yield fork(jobCardsListViewPivotTable)
    yield fork(jobCardQuoteDocumentsPivotTable)
    yield fork(jobCardQuoteItemsPivotTable)
  }

  return {
    watcher,
  }
}
